import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Breadcrumbs, CircularProgress, Link as MuiLink, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import JumboListNoDataPlaceholder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder";
import NoDataPlaceholder from "app/shared/NoDataPlaceholder";
import { DsokManagementListToolbar } from "app/pages/customers/dsok/components/DsokManagementListToolbar";
import { listPosUsers, resolvePosUserId } from "app/services/api/accessSystemPosUsers";
import PosUserListItem from "./users/PosUserListItem";
import PosUserFormModal from "./users/PosUserFormModal";

/** JumboSearch persistence key — same pattern as AccessSystemVouchersPage */
const POS_USERS_SEARCH_PAGE = "DsokPosUsers";

function filterPosUsers(rows, queryRaw) {
  const q = String(queryRaw ?? "")
    .trim()
    .toLowerCase();
  if (!q) return rows || [];
  return (rows || []).filter((row) => {
    const first = String(row.firstName ?? row.first_name ?? "").toLowerCase();
    const last = String(row.lastName ?? row.last_name ?? "").toLowerCase();
    const pin = String(row.pin ?? "").toLowerCase();
    const active =
      row.active !== false
        ? "yes active active 1 true"
        : "no inactive inactive 0 false";
    return (
      first.includes(q) ||
      last.includes(q) ||
      pin.includes(q) ||
      active.includes(q)
    );
  });
}

const DsokPosUsersPage = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedUser, setSelectedUser] = useState(null);

  const title = t("pages.pos.usersTitle");

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const list = await listPosUsers();
      setItems(Array.isArray(list) ? list : []);
      setLoadError(false);
    } catch (e) {
      console.error(e);
      setLoadError(true);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRows = useMemo(
    () => filterPosUsers(items, searchQuery),
    [items, searchQuery],
  );

  const openCreate = () => {
    setModalMode("create");
    setSelectedUser(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setModalMode("edit");
    setSelectedUser(row);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedUser(null);
  };

  const searchPlaceholder = t("pages.pos.searchPlaceholder");
  const addTooltip = t("pages.pos.users.listAddTooltip", {
    defaultValue: t("pages.pos.users.addButton"),
  });
  const loadErrorMessage = t("pages.pos.users.loadError");

  const renderMainContent = () => {
    if (loading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
          sx={{
            width: "100%",
            backgroundColor: "background.paper",
            borderRadius: 1,
            boxShadow: 1,
            py: 4,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (loadError) {
      return (
        <Box
          sx={{
            width: "100%",
            backgroundColor: "background.paper",
            borderRadius: 1,
            boxShadow: 1,
            p: 2,
          }}
        >
          <Alert severity="error">{loadErrorMessage}</Alert>
        </Box>
      );
    }

    if (!items.length) {
      return (
        <JumboListNoDataPlaceholder>
          <NoDataPlaceholder />
        </JumboListNoDataPlaceholder>
      );
    }

    if (!filteredRows.length) {
      return (
        <JumboListNoDataPlaceholder>
          <NoDataPlaceholder />
        </JumboListNoDataPlaceholder>
      );
    }

    return (
      <>
        {filteredRows.map((row, idx) => {
          const id = resolvePosUserId(row);
          const key = id != null ? String(id) : `row-${idx}`;
          return (
            <PosUserListItem
              key={key}
              row={row}
              onEdit={openEdit}
            />
          );
        })}
      </>
    );
  };

  return (
    <React.Fragment>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link to="/">{t("sidebar.menuItem.home")}</Link>
        <MuiLink component={Link} to="/customer/dsok" color="inherit" underline="hover">
          {t("pages.pos.breadcrumbCustomer")}
        </MuiLink>
        <Typography color="text.secondary">{t("sidebar.menu.pos")}</Typography>
        <Typography color="text.primary">{title}</Typography>
      </Breadcrumbs>

      <Typography variant="h2" mb={3}>
        {title}
      </Typography>

      <DsokManagementListToolbar
        searchPage={POS_USERS_SEARCH_PAGE}
        onSearchChange={setSearchQuery}
        searchPlaceholder={searchPlaceholder}
        addTooltip={addTooltip}
        onAddClick={openCreate}
        addAriaLabel={addTooltip}
      />

      {renderMainContent()}

      <PosUserFormModal
        open={modalOpen}
        mode={modalMode}
        user={selectedUser}
        onClose={closeModal}
        onSaved={load}
      />
    </React.Fragment>
  );
};

export default DsokPosUsersPage;
